import type { ReactElement } from 'react';
import type { MicIssue } from '@orborix/voice-client';
import { isNative, nativePlatform } from '../../lib/platform.js';
import { IconMicOff, IconMicSolid, IconRefresh } from './icons.js';
import type { MicRetryPhase } from './hooks/useSessionLifecycle.js';

interface MicIssueCardProps {
  /** `'denied'` = el user negó el permiso; `'unavailable'` = no hay mic usable. */
  issue: MicIssue;
  phase: MicRetryPhase;
  /** Pide el permiso (o vuelve a buscar el device) desde el gesto del user. */
  onRequest: () => void;
  /**
   * El audio del sistema no responde (ver `lib/audioStuck.ts`): la charla va
   * por escrito en las dos direcciones y buscar el mic de nuevo no sirve.
   */
  audioStuck?: boolean;
}

/**
 * Dónde habilitar el mic a mano cuando el browser ya bloqueó el sitio y su
 * prompt no vuelve a aparecer. Es lo único que depende de la plataforma: el
 * resto del flujo (pedir permiso, recuperar la voz) es igual en todas.
 *
 * El nombre que se ve en los ajustes del OS es el `appName` de
 * `capacitor.config.ts` ("Orborix Speak"); en web el permiso es del browser,
 * por sitio.
 */
function settingsHint(): string {
  if (isNative) {
    return nativePlatform() === 'ios'
      ? 'En Ajustes › Orborix Speak › Micrófono.'
      : 'En Ajustes › Aplicaciones › Orborix Speak › Permisos › Micrófono.';
  }
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) {
    return 'En Safari: tocá «ᴀA» a la izquierda de la dirección › Ajustes del sitio web › Micrófono › Permitir.';
  }
  return 'En el navegador: tocá el candado a la izquierda de la dirección › Micrófono › Permitir.';
}

/** Qué dice y qué ofrece el aviso en cada combinación de causa × fase. */
function copyFor(issue: MicIssue, phase: MicRetryPhase): { text: ReactElement; label: string } {
  if (issue === 'unavailable') {
    return {
      text:
        phase === 'retry' || phase === 'blocked' ? (
          <>
            Seguimos sin encontrar un micrófono disponible. Revisá que esté conectado y que no lo
            esté usando otra app, y tocá Buscar de nuevo.
          </>
        ) : (
          <>
            No encontramos un micrófono, así que escribile al coach acá abajo. Si conectás uno (o
            cerrás la app que lo esté usando), buscalo de nuevo y seguimos por voz.
          </>
        ),
      label: phase === 'asking' ? 'Buscando…' : 'Buscar de nuevo',
    };
  }
  if (phase === 'blocked') {
    return {
      text: (
        <>
          {isNative
            ? 'El micrófono está desactivado para Orborix Speak en los ajustes, así que ya no se puede pedir desde acá.'
            : 'El navegador tiene el micrófono bloqueado para este sitio, así que ya no pregunta.'}{' '}
          Habilitalo a mano y tocá Reintentar: la charla sigue acá. {settingsHint()}
        </>
      ),
      label: 'Reintentar',
    };
  }
  if (phase === 'retry') {
    return {
      text: (
        <>
          No llegó a darse el permiso. Tocá de nuevo y elegí <b>Permitir</b> en el cartel del{' '}
          {isNative
            ? 'sistema — la charla sigue acá. Ojo: si lo rechazás, el sistema deja de preguntar y hay que activarlo en los ajustes.'
            : 'navegador — la charla sigue acá. Ojo: si lo descartás varias veces seguidas, el navegador deja de preguntar por un tiempo.'}
        </>
      ),
      label: 'Volver a pedir permiso',
    };
  }
  return {
    text: (
      <>
        No tenemos permiso para usar el micrófono, así que escribile al coach acá abajo. Si querés
        hablar, dale permiso y seguimos por voz en esta misma charla.
      </>
    ),
    label: phase === 'asking' ? 'Pidiendo permiso…' : 'Activar el micrófono',
  };
}

/**
 * Aviso de la charla degradada a sólo-texto por el mic, CON el camino de vuelta
 * a la voz (card #315: antes el aviso sólo explicaba "activá el permiso y volvé
 * a entrar" — el user tenía que salirse de la charla para recuperar la voz).
 *
 * No es una card de error a propósito: la sesión está viva y el coach ya está
 * saludando abajo (ver la política de degradar en `useSessionLifecycle`), así
 * que esto informa qué canal quedó y ofrece la acción — no corta nada.
 *
 * El botón es la pieza clave: el pre-flight del arranque corre SIN gesto del
 * user, y en ese contexto un prompt descartado (la X de Chrome, el "Don't
 * Allow" de iOS) no vuelve a aparecer. El mismo pedido bajo un tap SÍ promptea.
 *
 * Lo que decide el discurso cuando el pedido rebota NO es el error (idéntico
 * para descarte y bloqueo) sino el estado del permiso, que resuelve el hook:
 * en `'retry'` el botón sigue siendo la acción y el copy avisa del auto-bloqueo
 * que viene si lo descarta varias veces (Chrome corta a las 3); en `'blocked'`
 * ya no hay prompt posible y el único camino honesto son los ajustes.
 */
export function MicIssueCard({
  issue,
  phase,
  onRequest,
  audioStuck = false,
}: MicIssueCardProps): ReactElement {
  if (audioStuck) {
    return (
      <div className="card mic-issue-card" role="status">
        <div className="mic-issue-head">
          <span className="mic-issue-glyph" aria-hidden="true">
            <IconMicOff size={16} />
          </span>
          <div className="mic-issue-text">
            <b>{isNative ? 'El audio del dispositivo no responde.' : 'El audio de esta compu no responde.'}</b>{' '}
            La charla sigue por escrito: escribile al coach acá abajo y leé sus respuestas (no se
            van a escuchar).{' '}
            {isNative
              ? 'Para volver a la voz, desconectá y volvé a conectar los auriculares o cerrá y abrí la app, y entrá de nuevo.'
              : 'Para volver a la voz, apagá y prendé los auriculares o reiniciá la compu, y entrá de nuevo.'}
          </div>
        </div>
      </div>
    );
  }
  const { text, label } = copyFor(issue, phase);
  const asking = phase === 'asking';
  const retrying = phase === 'retry' || phase === 'blocked';
  return (
    <div className="card mic-issue-card" role="status">
      <div className="mic-issue-head">
        <span className="mic-issue-glyph" aria-hidden="true">
          <IconMicOff size={16} />
        </span>
        <div className="mic-issue-text">
          <b>Esta charla va por escrito.</b> {text}
        </div>
      </div>
      <div className="mic-issue-actions">
        <button
          type="button"
          className="conn-btn conn-btn-primary"
          onClick={onRequest}
          disabled={asking}
        >
          {retrying ? <IconRefresh size={14} /> : <IconMicSolid size={14} />} {label}
        </button>
      </div>
    </div>
  );
}
